import { useEffect, useMemo, useState } from "react"
import { useShopify } from "@app/hooks/useShopify"
import { NormalisedProduct } from "@root/types/product"
import { useQuery } from "@apollo/client"
import { GET_PRODUCTS_BY_HANDLE_LIGHT } from "@app/graphql/queries/minimalProduct"
import { useInView } from "react-intersection-observer"

export const useLiveProducts = (products: any, useProductsPlaceholder = true, disabled = false, lazyLoad = false) => {
  const { getHandle, productNormaliser } = useShopify()

  const isAtStartScroll = useMemo(() => (typeof window !== "undefined" ? window.scrollY === 0 : true), [])
  const [ref, inView] = useInView({
    triggerOnce: true,
    initialInView: !isAtStartScroll || !lazyLoad,
    skip: !isAtStartScroll || !lazyLoad,
    rootMargin: "1200px",
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const productHandles = useMemo(() => products?.map((item: any) => getHandle(item)).filter(Boolean), [products])

  const { data, loading } = useQuery(GET_PRODUCTS_BY_HANDLE_LIGHT(productHandles?.length ? productHandles : [""]), {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    variables: {
      countryCode: "AU",
      handles: [],
      firstCollections: 0,
      firstImages: 2,
      firstMedia: 0,
      firstMetafields: 0,
      firstVariants: 1,
    },
    skip: disabled || !productHandles?.length || lazyLoad ? !inView : false,
  })

  // Match live data back to each product by its own handle, not its index in `productHandles`:
  // handle-less products (e.g. weak references to deleted products) are filtered out of that list.
  const toLiveProducts = (dict: any) =>
    products
      ?.map((product: any) => {
        const normalisedLiveProduct = productNormaliser(dict[`product${getHandle(product)?.replace(/-/g, "")}`])
        return normalisedLiveProduct?.handle
          ? {
              ...normalisedLiveProduct,
              primaryCollection: product?.primaryCollection,
              siblings: product?.siblings,
            }
          : null
      })
      ?.filter(Boolean)

  const [liveProducts, setLiveProducts] = useState<NormalisedProduct[]>(
    data ? toLiveProducts(data) : useProductsPlaceholder ? products : []
  )

  useEffect(() => {
    if (data) setLiveProducts(toLiveProducts(data))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return {
    liveProducts: liveProducts?.length ? liveProducts : [],
    loading,
    inView,
    inViewRef: ref,
  }
}
